export enum BadgeCategory {
  ClimatePositive = 'climate-positive',
  TreesPlanted = 'trees-planted',
  SparklyTrees = 'sparkly-trees',
  CarbonReduction = 'carbon-reduction',
  Mta = 'mta-badge',
}

export type BadgeVisibility = 'all' | 'business-only' | 'member-only' | 'mta-only';

// These IDs need to be kept in sync with those in sls/emails/src/badge-emails.ts
export type BadgeId =
  | 'trees100'
  | 'trees500'
  | 'trees1000'
  | 'trees5000'
  | 'trees10000'
  | 'trees50000'
  | 'trees100000'
  | 'trees250000'
  | 'trees500000'
  | 'trees750000'
  | 'trees1000000'
  | 'sparklyTrees25'
  | 'sparklyTrees150'
  | 'sparklyTrees300'
  | 'sparklyTrees500'
  | 'climatePositiveMonths3'
  | 'climatePositiveMonths6'
  | 'climatePositiveMonths12'
  | 'climatePositiveMonths24'
  | 'climatePositiveMonths36'
  | 'offsetTonnes10'
  | 'offsetTonnes50'
  | 'offsetTonnes100'
  | 'offsetTonnes1000'
  | 'mtpBadge';

// Response from API
export type BadgeType = { id: BadgeId; createdAt: string };

// Hydrated form of BadgeType
export interface Badge {
  readonly id: BadgeId;
  readonly title: string;
  readonly visibility: BadgeVisibility;
  readonly category: BadgeCategory;
  readonly total: number;
  readonly standoutTotal: string;
  readonly copy: string;
  readonly createdAt?: Date;
}

export const availableBadges: { readonly [id in BadgeId]: Badge } = {
  mtpBadge: {
    id: 'mtpBadge',
    title: 'Million Tree Pledger',
    visibility: 'mta-only',
    total: 0,
    standoutTotal: '0',
    copy: "You committed to planting over 1 million trees in response to the Climate Emergency. We're proud to be supporting you on this journey and think you're pretty awesome!",
    category: BadgeCategory.Mta,
  },
  trees100: {
    id: 'trees100',
    title: '100 trees funded',
    visibility: 'all',
    total: 100,
    standoutTotal: '100',
    copy: "Look at that, you've funded 100 trees! You're well on your way to your own little forest.",
    category: BadgeCategory.TreesPlanted,
  },
  trees500: {
    id: 'trees500',
    title: '500 trees funded',
    visibility: 'all',
    total: 500,
    standoutTotal: '500',
    copy: "Look at how you've grown! By this point your forest will be full of life and helping to repair our planet.",
    category: BadgeCategory.TreesPlanted,
  },
  trees1000: {
    id: 'trees1000',
    title: '1,000 trees funded',
    visibility: 'all',
    total: 1000,
    standoutTotal: '1K',
    copy: "Woah, that's a lot of trees! Consider yourself a tree champion because we sure do.",
    category: BadgeCategory.TreesPlanted,
  },
  trees5000: {
    id: 'trees5000',
    title: '5,000 trees funded',
    visibility: 'all',
    total: 5000,
    standoutTotal: '5K',
    copy: "Stop everything you're doing, you've funded over 5,000 trees. This. Is. Epic. Just think, by now your forest will be the home to lots of critters large and small.",
    category: BadgeCategory.TreesPlanted,
  },
  trees10000: {
    id: 'trees10000',
    title: '10,000 trees funded',
    visibility: 'all',
    total: 10000,
    standoutTotal: '10K',
    copy: "You've funded a goliath amount of trees! I mean, we're impressed to say the least. Keep going, keep planting, keep being awesome.",
    category: BadgeCategory.TreesPlanted,
  },
  trees50000: {
    id: 'trees50000',
    title: '50,000 trees funded',
    visibility: 'all',
    total: 50000,
    standoutTotal: '50K',
    copy: "50,000 trees! That's right — just think of the difference you're making to the planet. Keep doing what you're doing, and carry on making a positive impact!",
    category: BadgeCategory.TreesPlanted,
  },
  trees100000: {
    id: 'trees100000',
    title: '100,000 trees funded',
    visibility: 'all',
    total: 100000,
    standoutTotal: '100K',
    copy: "Outstanding! You've funded your 100,000th tree, this an achievement of gargantuan proportions. Let the world know!",
    category: BadgeCategory.TreesPlanted,
  },
  trees250000: {
    id: 'trees250000',
    title: '250,000 trees funded',
    visibility: 'all',
    total: 250000,
    standoutTotal: '250K',
    copy: "You're on a roll! You've funded a quarter of a million trees in forests across the globe. You're well on your way to making a substantial impact for generations to come. Well done!",
    category: BadgeCategory.TreesPlanted,
  },
  trees500000: {
    id: 'trees500000',
    title: '500,000 trees funded',
    visibility: 'all',
    total: 500000,
    standoutTotal: '500K',
    copy: "You're awesome, and you've just funded your 500,000th tree. We really love you for it! Keep up the good work, champ!",
    category: BadgeCategory.TreesPlanted,
  },
  trees750000: {
    id: 'trees750000',
    title: '750,000 trees funded',
    visibility: 'all',
    total: 750000,
    standoutTotal: '750K',
    copy: "We bow down to you, awesome eco-warrior! You've funded 750,000 trees. Just think of the amazing forest you're helping to grow, and all the life it will sustain in the future. Go you!",
    category: BadgeCategory.TreesPlanted,
  },
  trees1000000: {
    id: 'trees1000000',
    title: '1,000,000 trees funded',
    visibility: 'all',
    total: 1000000,
    standoutTotal: '1M',
    copy: "You've done something truly amazing… YOU'VE FUNDED 1,000,000 TREES!",
    category: BadgeCategory.TreesPlanted,
  },
  sparklyTrees25: {
    id: 'sparklyTrees25',
    title: '25 sparkly trees funded',
    visibility: 'all',
    total: 25,
    standoutTotal: '25',
    copy: "Look at them shining! Nice work, you're well on your way to a big ol' sparkly forest.",
    category: BadgeCategory.SparklyTrees,
  },
  sparklyTrees150: {
    id: 'sparklyTrees150',
    title: '150 sparkly trees funded',
    visibility: 'all',
    total: 150,
    standoutTotal: '150',
    copy: "You've got more sparkly trees than you can shake a stick at.",
    category: BadgeCategory.SparklyTrees,
  },
  sparklyTrees300: {
    id: 'sparklyTrees300',
    title: '300 sparkly trees funded',
    visibility: 'all',
    total: 300,
    standoutTotal: '300',
    copy: 'By this point your forest is brighter than the sun!',
    category: BadgeCategory.SparklyTrees,
  },
  sparklyTrees500: {
    id: 'sparklyTrees500',
    title: '500 sparkly trees funded',
    visibility: 'all',
    total: 500,
    standoutTotal: '500',
    copy: "Cor blimey, you've got 500 sparkly trees in your forest. Amazing work, keep it up!",
    category: BadgeCategory.SparklyTrees,
  },
  climatePositiveMonths3: {
    id: 'climatePositiveMonths3',
    title: '3 months of climate action',
    visibility: 'all',
    total: 3,
    standoutTotal: '3',
    copy: "Well lookie here, you've achieved 3 months of climate impact! Give yourself a big high five.",
    category: BadgeCategory.ClimatePositive,
  },
  climatePositiveMonths6: {
    id: 'climatePositiveMonths6',
    title: '6 months of climate action',
    visibility: 'all',
    total: 6,
    standoutTotal: '6',
    copy: "Gosh would you look at that, you've ticked off 6 whole months of climate impact.",
    category: BadgeCategory.ClimatePositive,
  },
  climatePositiveMonths12: {
    id: 'climatePositiveMonths12',
    title: '1 year of climate action',
    visibility: 'all',
    total: 12,
    standoutTotal: '12',
    copy: 'What a difference a year makes. Seriously, 12 months is a big deal so take a moment to sit back and soak it up.',
    category: BadgeCategory.ClimatePositive,
  },
  climatePositiveMonths24: {
    id: 'climatePositiveMonths24',
    title: '2 years of climate action',
    visibility: 'all',
    total: 24,
    standoutTotal: '24',
    copy: "You're starting to make this look easy! Keep the amazing work, the planet needs more folks like you.",
    category: BadgeCategory.ClimatePositive,
  },
  climatePositiveMonths36: {
    id: 'climatePositiveMonths36',
    title: '3 years of climate action',
    visibility: 'all',
    total: 36,
    standoutTotal: '36',
    copy: "You're the ultimate climate crisis fighting machine! Congratulations, you're charging ahead and doing your bit for the planet.",
    category: BadgeCategory.ClimatePositive,
  },
  offsetTonnes10: {
    id: 'offsetTonnes10',
    title: '10 tonnes of CO2e avoided',
    visibility: 'all',
    total: 10,
    standoutTotal: '10',
    copy: "Look at that, you've avoided 10 tonnes of CO2e in our atmosphere. That's the equivalent of not charging your phone 1,275,322 times.",
    category: BadgeCategory.CarbonReduction,
  },
  offsetTonnes50: {
    id: 'offsetTonnes50',
    title: '50 tonnes of CO2e avoided',
    visibility: 'all',
    total: 50,
    standoutTotal: '50',
    copy: "Woah there, that's 50 tonnes of CO2e you've prevented from entering the atmosphere. That's the same amount of carbon that would be produced by 4,912 gallons of diesel.",
    category: BadgeCategory.CarbonReduction,
  },
  offsetTonnes100: {
    id: 'offsetTonnes100',
    title: '100 tonnes of CO2e avoided',
    visibility: 'all',
    total: 100,
    standoutTotal: '100',
    copy: "Amazing! You've avoided 100 tonnes of CO2e in our planet's atmosphere. This amount of carbon is equivalent to 248,139 miles driven in a car. Crazy right?",
    category: BadgeCategory.CarbonReduction,
  },
  offsetTonnes1000: {
    id: 'offsetTonnes1000',
    title: '1,000 tonnes of CO2e avoided',
    visibility: 'all',
    total: 1000,
    standoutTotal: '1K',
    copy: "Golly gosh, you've avoided 1,000 tonnes of CO2e running rampant in our Earth's atmosphere. That's the equivalent of over 600 flights from London to New York! Keep up the incredible work.",
    category: BadgeCategory.CarbonReduction,
  },
};

const getValues = <T>(obj: { readonly [key: string]: T }) => {
  const keys = Object.keys(obj);
  return keys.map(k => obj[k]);
};

export const climatePositiveBadges: ReadonlyArray<Badge> = getValues<Badge>(availableBadges).filter(
  b => b.category === 'climate-positive',
);
export const treeBadges: ReadonlyArray<Badge> = getValues<Badge>(availableBadges).filter(
  b => b.category === 'trees-planted',
);
export const sparklyBadges: ReadonlyArray<Badge> = getValues<Badge>(availableBadges).filter(
  b => b.category === 'sparkly-trees',
);
export const carbonReductionBadges: ReadonlyArray<Badge> = getValues<Badge>(availableBadges).filter(
  b => b.category === 'carbon-reduction',
);

export const mtpBadge: Badge = availableBadges.mtpBadge;
